import { useAtom } from '@reatom/npm-react'
import { memo, useMemo } from 'react'
import styled from 'styled-components'

import { OTHER_RANK_COLOR, RANK_COLOR } from './constants'
import { PairRankIcon } from './pair-rank-icon'

import type { PairModel } from '../../atoms'
import type { ClassNameProps } from '@root/shared/react'

type Size = 'xs' | 'sm' | 'm'

type TrendingRankProps = {
  size?: Size
} & ClassNameProps &
  (
    | { pair: PairModel; rank?: undefined }
    | { rank: number | undefined; pair?: undefined }
  )

export const PairRank: React.FC<TrendingRankProps> = memo(
  ({ className, size = 'sm', pair, rank: rankProp }) => {
    const [rank] = useAtom(
      (ctx) => {
        if (pair) {
          return ctx.spy(pair.rank)
        }
        return rankProp
      },
      [pair, rankProp],
    )

    const rankFormatted = useMemo(
      () => rank?.toString().padStart(2, '0') ?? '',
      [rank],
    )

    if (!rank || rank > 99) {
      return undefined
    }

    const variables = {
      '--color': RANK_COLOR[rank] ?? OTHER_RANK_COLOR,
    } as React.CSSProperties

    return (
      <Root $size={size} className={className} style={variables}>
        <PairRankIcon size={ICON_SIZE_MAP[size]} rank={rank} />
        <span>#{rankFormatted}</span>
      </Root>
    )
  },
)

const Root = styled.div<{ $size: Size }>`
  display: grid;
  grid-template-columns: auto auto;
  gap: 2px;
  align-items: center;
  justify-items: start;
  justify-content: start;

  color: var(--color);

  font-size: ${({ $size }) => SIZE_MAP[$size]}px;
  font-weight: 700;
`

const SIZE_MAP: Record<Size, number> = {
  xs: 11,
  sm: 12,
  m: 16,
}

const ICON_SIZE_MAP: Record<Size, number> = {
  xs: 8,
  sm: 10,
  m: 13,
}
